import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Typography,
} from '@material-ui/core';
import './index.scss';
import CloseIconModal from 'assets/icons/CloseIconModal';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Row } from 'components/Row';
import { Col } from 'components/Col';
import { NewAutoCompleteField } from 'components/NewAutoCompleteField';
import { getFormattedCNPJ } from 'utils/DataFormatUtils';
import { CurrencyNewInput } from 'components/CustomCurrencyField/CurrencyNewInput';
import { CustomTextField } from 'components/CustomTextField';
import { CustomCheckbox } from 'components/CustomCheckbox';
import {
  changeContratoAssessorEditingValue,
  closeModalAditivo,
  contratoAssessorDescentralizadoCreateAditivo,
  contratoAssessorDescentralizadoEditAditivo,
  loadContratoAssessor,
} from 'store/modules/ContratosFluxoDescentralizado/Assessores/AssessoresActions';
import useValidations from 'pages/Contratos/Fluxos/FluxoDescentralizados/hooks/useValidations';
import { diferenceMonths } from 'utils/DateUtils';
import moment from 'moment';
import { DateMaskedInput } from 'components/MaskedInputs';
import { Assessor, Empresa } from '../../__types';
import SnackbarUtils from 'utils/SnackbarUtils';
import {
  CONTRATO_ETAPAS_PARAMS_URL,
  CONTRATO_PASSOS,
  CONTRATO_FLUXO_DESCENTRALIZADO,
  ContratoStatus,
  URL_ADITIVO_PARAM,
  VersaoContratoEnum,
  URL_ENTE_DESCENTRALIZADO,
} from 'models/contratos';
import ModalConfirm from 'pages/Contratos/components/ModalConfirm';
import { openModal } from 'store/modules/ModalActions';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { newContratoDescentralizadoEditRoute } from 'library/Routes';
import {
  CreateRouteParams,
  EditRouteParams,
} from 'pages/Contratos/Fluxos/FluxoDescentralizados/__types';
import { loadContratoAdendos } from 'store/modules/AllContratos/AllContratosActions';
import { loadAllEmpresas } from 'store/modules/EmpresasActions';

const ModalAditivoAssessor = () => {
  const languageValues = useSelector(
    (state: RootState) => state.languages.languageValues
  );
  const [dataInicioError, setDataInicioError] = useState('');
  const [dataFimError, setDataFimError] = useState('');
  const contratosAssessoresReducer = useSelector(
    (state: RootState) => state.contratosDescentralizadosAssessores
  );
  const empresasReducer = useSelector((state: RootState) => state.empresas);
  const adendosAll = useSelector(
    (state: RootState) => state.allContratos.adendosAll
  );
  const adendoVersaoContratoUm = adendosAll.find(
    (adendo) => adendo.versaoContrato === 1
  );
  const idVersaoContrato = adendoVersaoContratoUm
    ? adendoVersaoContratoUm?.id
    : null;
  const {
    valorAssessoriaIsValid,
    valorFormacaoIsValid,
    valorReuniaoIsValid,
    validDadosAssessorAditivo,
    desabilitaCampoEspecificoAditivoEDistrato,
    dataInicioIsValid,
    dataFimIsValid,
    validateDataInicio,
    validateDataFim
  } = useValidations();
  const location = useLocation();
  const params = useParams<CreateRouteParams & EditRouteParams>();
  const history = useHistory();
  const dispatch = useDispatch();

  const fluxoAssessor = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ASSESSOR);

  const handleDataChange = useCallback(
    (name, value) => {
      dispatch(changeContratoAssessorEditingValue(name, value));
    },
    [dispatch]
  );

  const handleCnpjChange = useCallback(
    (value) => {
      if (value) {
        const currValue = value.value !== '' ? value : '';
        handleDataChange('idEmpresa', currValue.value);
        handleDataChange('cnpjEmpresa', currValue.cnpj);
      } else {
        handleDataChange('idEmpresa', '');
        handleDataChange('cpfAssessores', []);
      }
    },
    [handleDataChange]
  );

  const handleAssessorChange = (value: Assessor[]) => {
    const currValue =
      Array.isArray(value) && value.length > 0
        ? value.map((it) => it.value)
        : [];
    handleDataChange('cpfAssessores', currValue);
  };

  const onDataInicioChanged = (value: string) => {
    const error = validateDataInicio(
      value,
      contratosAssessoresReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );
    setDataInicioError(error);

    if (!error) {
      setDataInicioError('');
    }

    let months: unknown = diferenceMonths(
      value,
      contratosAssessoresReducer.contratoEditing.dataFim
    );
    if (typeof months === 'string') {
      months = parseFloat(months);
    }
    if (isNaN(months as number)) {
      months = 0;
    }
    handleDataChange('totalMeses', Math.max(months as number, 0));
  };

  const onDataFimChanged = (value: string) => {
    const error = validateDataFim(
      value,
      contratosAssessoresReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    setDataFimError(error);

    if (!error) {
      setDataFimError('');
    }
    const dataInicioErrorValue = validateDataInicio(
      contratosAssessoresReducer.contratoEditing.dataInicio,
      value,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );
    setDataInicioError(dataInicioErrorValue);

    let months: unknown = diferenceMonths(
      contratosAssessoresReducer.contratoEditing.dataInicio,
      value
    );
    if (typeof months === 'string') {
      months = parseFloat(months);
    }
    if (isNaN(months as number)) {
      months = 0;
    }
    handleDataChange('totalMeses', Math.max(months as number, 0));
  };

  const dataInicioValidacaoAditivoResultado = dataInicioIsValid(
    contratosAssessoresReducer.contratoEditing.dataInicio,
    contratosAssessoresReducer.contratoEditing.dataFim,
    {
      after: languageValues.dataInicioError,
      same: languageValues.dataInicioIgualDataFimError,
    }
  );

  const dataFimValidacaoAditivoResultado = dataFimIsValid(
    contratosAssessoresReducer.contratoEditing.dataFim,
    contratosAssessoresReducer.contratoEditing.dataInicio,
    {
      before: languageValues.dataFimError,
      same: languageValues.dataFimIgualDataInicioError,
    }
  );

  const fechaModalEAtualizaContrato = () => {
    history.push(
      newContratoDescentralizadoEditRoute.getURLWithParams(
        CONTRATO_FLUXO_DESCENTRALIZADO.ASSESSOR,
        CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
        idVersaoContrato,
        CONTRATO_PASSOS.DOCUMENTOS
      )
    );
    dispatch(closeModalAditivo());
    dispatch(
      loadContratoAssessor(idVersaoContrato, VersaoContratoEnum.CONTRATO)
    );
  };

  const verificaSeTemAditivo = () => {
    return adendosAll.some(
      (item) => item.versaoContrato === VersaoContratoEnum.ADITIVO
    );
  };
  const contenAditivo = verificaSeTemAditivo();

  const submitAditivo = async () => {
    const isEdicaoAditivo = location.pathname.includes(
      URL_ADITIVO_PARAM.EDICAO_ADITIVO
    );
    const isVersaoAditivo = location.pathname.includes(
      URL_ADITIVO_PARAM.VERSAO
    );
    const isCriacao = location.pathname.includes(
      URL_ADITIVO_PARAM.CADASTRO_ADITIVO
    );
    const aditivoData = {
      idContrato: idVersaoContrato,
      idEmpresa: contratosAssessoresReducer.contratoEditing.idEmpresa,
      cnpjEmpresa: contratosAssessoresReducer.contratoEditing.cnpjEmpresa,
      cpfAssessores: contratosAssessoresReducer.contratoEditing.cpfAssessores,
      valorAssessoria:
        contratosAssessoresReducer.contratoEditing.valorAssessoria,
      valorFormacao: contratosAssessoresReducer.contratoEditing.valorFormacao,
      valorReuniao: contratosAssessoresReducer.contratoEditing.valorReuniao,
      valorOutros: contratosAssessoresReducer.contratoEditing.valorOutros,
      dataInicio: contratosAssessoresReducer.contratoEditing.dataInicio,
      dataFim: contratosAssessoresReducer.contratoEditing.dataFim,
      flgAnaliseJuridica:
        contratosAssessoresReducer.contratoEditing.flgAnaliseJuridica,
      totalMeses: contratosAssessoresReducer.contratoEditing.totalMeses,
      versaoContrato: contratosAssessoresReducer.contratoEditing.versaoContrato,
    };
    const data = { ...aditivoData };

    try {
      const formattedDataInicio = moment(data.dataInicio, 'DD/MM/YYYY');
      const formattedDataFim = moment(data.dataFim, 'DD/MM/YYYY');
      data.dataInicio = formattedDataInicio.format('YYYY-MM-DD');
      data.dataFim = formattedDataFim.format('YYYY-MM-DD');

      if (isVersaoAditivo && isEdicaoAditivo) {
        data.idContrato = idVersaoContrato;
        dispatch(
          contratoAssessorDescentralizadoEditAditivo(
            Number(params.idAditivo),
            data,
            () => {
              SnackbarUtils.enqueueSnackbar({
                message: languageValues.successContratoAditivoAtualizar,
                options: {
                  variant: 'success',
                },
              });
              fechaModalEAtualizaContrato();
            },
            (error) => {
              const errorMessage = error.response?.data?.message;
              SnackbarUtils.enqueueSnackbar({
                message: errorMessage,
                options: {
                  variant: 'error',
                },
              });
            }
          )
        );
      } else if (isCriacao && (contenAditivo || !contenAditivo)) {
        data.idContrato = idVersaoContrato;
        dispatch(
          contratoAssessorDescentralizadoCreateAditivo(
            data,
            () => {
              SnackbarUtils.enqueueSnackbar({
                message: languageValues.successContratoAditivoSalvar,
                options: {
                  variant: 'success',
                },
              });
              fechaModalEAtualizaContrato();
              dispatch(loadContratoAdendos(idVersaoContrato));
            },
            (error) => {
              const errorMessage = error.response?.data?.message;
              SnackbarUtils.enqueueSnackbar({
                message: errorMessage,
                options: {
                  variant: 'error',
                },
              });
            }
          )
        );
      }
    } catch (error) {
      SnackbarUtils.enqueueSnackbar({
        message: error,
        options: {
          variant: 'error',
        },
      });
    }
  };

  const abrirModalCriacaoAditivo = () => {
    const fechaModal = !desabilitaCampos();
    const status = contratosAssessoresReducer.contratoEditing.flgStatus;

    if (fechaModal) {
      fechaModalEAtualizaContrato();
      return;
    }
    if (status === ContratoStatus.EM_EDICAO && contenAditivo === true) {
      submitAditivo();
      fechaModalEAtualizaContrato();
      return;
    }
    dispatch(
      openModal(ModalConfirm, {
        title: languageValues.criacaoAditivo,
        content: languageValues.confirmarCriacaoAditivo,
        confirmLabel: languageValues.confirmar,
        confirmCallback: () => {
          submitAditivo();
        },
        modalKey: 'modalComplete',
        maxWidth: 'sm',
      })
    );
  };

  const tituloModalAditivo = () => {
    const status = contratosAssessoresReducer.contratoEditing.flgStatus;
    const url = window.location.href;

    if (url.includes(URL_ADITIVO_PARAM.EDICAO_ADITIVO)) {
      switch (status) {
        case ContratoStatus.ENCERRADO:
        case ContratoStatus.NAO_INICIADO:
        case ContratoStatus.VIGENTE:
          return `${languageValues.aditivo}`;
        default:
          return `${languageValues.editarAditivo}`;
      }
    }

    return languageValues.criacaoAditivo;
  };

  const desabilitaCampos = () => {
    const isCriacao = location.pathname.includes(URL_ADITIVO_PARAM.CADASTRO_ADITIVO);
    const { flgStatus, ativo } = contratosAssessoresReducer.contratoEditing;
    if (!ativo) {
      return false;
    }
    if (!isCriacao) {
      switch (flgStatus) {
        case ContratoStatus.ENCERRADO:
        case ContratoStatus.VIGENTE:
        case ContratoStatus.NAO_INICIADO:
          return false;
        default:
          return true;
      }
    }
    return true;
  };

  useEffect(() => {
    if (fluxoAssessor &&
      (location.pathname.includes(URL_ADITIVO_PARAM.MODAL_EDICAO_ADITIVO) ||
        location.pathname.includes(URL_ADITIVO_PARAM.EDICAO_ADITIVO))) {
      dispatch(loadContratoAssessor(Number(params.idAditivo), VersaoContratoEnum.ADITIVO));
      dispatch(loadAllEmpresas());
    }
  }, [dispatch, fluxoAssessor, location.pathname, params.idAditivo]);

  useEffect(() => {
    if (fluxoAssessor &&
      location.pathname.includes(URL_ADITIVO_PARAM.CADASTRO_ADITIVO)) {
      dispatch(loadContratoAssessor(Number(params.id), VersaoContratoEnum.CONTRATO));
      dispatch(loadAllEmpresas());
    }
  }, [contratosAssessoresReducer.contratoEditing.cnpjEmpresa,
  contratosAssessoresReducer.contratoEditing.idEmpresa, dispatch, fluxoAssessor, location.pathname, params.id])

  return (
    <>
      <Dialog
        open={contratosAssessoresReducer.isModalAditivoOpen}
        aria-labelledby='form-dialog-aditivo'
        className='form-dialog-aditivo'
        TransitionComponent={Fade}
        transitionDuration={{
          enter: 350,
        }}
        PaperProps={{
          style: {
            boxShadow: 'none',
          },
        }}
      >
        <DialogTitle className='form-dialog-aditivo__title-modal'>
          <div className='form-dialog-aditivo__titleContainer'>
            <Typography className='form-dialog-aditivo__titleModal'>
              {tituloModalAditivo()}
            </Typography>
            <IconButton
              aria-label='close'
              className='form-dialog-aditivo__closeButton'
              onClick={() => fechaModalEAtualizaContrato()}
            >
              <CloseIconModal />
            </IconButton>
          </div>
          <div className='form-dialog-aditivo__titleLine'></div>
        </DialogTitle>
        <DialogContent>
          <Box className='form-dialog-aditivo__box'>
            <Card className='form-dialog-aditivo__card'>
              <Box>
                <Row>
                  <Col cols={[12, 12, 12, 12, 12]}>
                    <Typography
                      variant='h5'
                      id='dadosAssessor'
                      className='form-dialog-aditivo__tipo-contrato-title'
                    >
                      {languageValues.cadastroContratoDadosAssessor}
                    </Typography>
                  </Col>
                </Row>
                <Row>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <NewAutoCompleteField
                      id={'empresa'}
                      label={languageValues.cadastroContratoEmpresaRequired}
                      options={empresasReducer.allList.map((item: Empresa) => ({
                        label: `${getFormattedCNPJ(item.cnpjEmpresa)} - ${item.nome
                          }`,
                        value: item.id,
                        cnpj: item.cnpjEmpresa,
                      }))}
                      value={
                        contratosAssessoresReducer.contratoEditing.idEmpresa
                      }
                      onChange={handleCnpjChange}
                      loading={empresasReducer.loadingAllList}
                      loadingMessage={languageValues.loadingAssessores}
                      disabled={!desabilitaCampoEspecificoAditivoEDistrato()}
                    />
                  </Col>

                  <Col cols={[12, 12, 6, 6, 6]}>
                    <NewAutoCompleteField
                      id={'assessor'}
                      label={languageValues.cadastroContratoAssessorRequired}
                      placeholder={
                        empresasReducer.loadingAllList ||
                          empresasReducer.loadingEmpresaByCnpjAssessoresNecessarios
                          ? languageValues.loadingAssessores
                          : ''
                      }
                      options={(
                        empresasReducer.empresaByCnpjAssessoresNecessarios
                          .assessores || []
                      ).map((item: Assessor) => ({
                        label: item.nome,
                        value: item.cpf,
                      }))}
                      multiple
                      value={
                        contratosAssessoresReducer.contratoEditing.cpfAssessores
                      }
                      onChange={handleAssessorChange}
                      loading={
                        empresasReducer.loadingAllList ||
                        empresasReducer.loadingEmpresaByCnpjAssessoresNecessarios
                      }
                      loadingMessage={languageValues.loadingAssessores}
                      disabled={!desabilitaCampoEspecificoAditivoEDistrato()}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CurrencyNewInput
                      id={'valor-hora-assessoria'}
                      label={languageValues.contratoValorAssessoriaRequired}
                      name={'valorAssessoria'}
                      value={
                        contratosAssessoresReducer.contratoEditing
                          .valorAssessoria
                      }
                      onChange={(value) => {
                        handleDataChange('valorAssessoria', value);
                      }}
                      error={!valorAssessoriaIsValid().status}
                      helperText={
                        !valorAssessoriaIsValid().status
                          ? valorAssessoriaIsValid().error
                          : ''
                      }
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CurrencyNewInput
                      id={'valor-hora-formacao'}
                      label={languageValues.contratoValorFormacaoRequired}
                      name={'valorFormacao'}
                      value={
                        contratosAssessoresReducer.contratoEditing.valorFormacao
                      }
                      onChange={(value) => {
                        handleDataChange('valorFormacao', value);
                      }}
                      error={!valorFormacaoIsValid().status}
                      helperText={
                        !valorFormacaoIsValid().status
                          ? valorFormacaoIsValid().error
                          : ''
                      }
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CurrencyNewInput
                      id={'valor-hora-reuniao'}
                      label={languageValues.contratoValorReuniaoRequired}
                      name={'valorReuniao'}
                      value={
                        contratosAssessoresReducer.contratoEditing.valorReuniao
                      }
                      onChange={(value) => {
                        handleDataChange('valorReuniao', value);
                      }}
                      error={!valorReuniaoIsValid().status}
                      helperText={
                        !valorReuniaoIsValid().status
                          ? valorReuniaoIsValid().error
                          : ''
                      }
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CurrencyNewInput
                      id={'valor-hora-outros'}
                      label={
                        languageValues.contratoAssessorPedagogicoOutrosType
                      }
                      name={'valorOutros'}
                      value={
                        contratosAssessoresReducer.contratoEditing.valorOutros
                      }
                      onChange={(value) => {
                        handleDataChange('valorOutros', value);
                      }}
                      error={false}
                      helperText={''}
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col cols={[12, 12, 12, 12, 12]}>
                    <Typography
                      variant='h5'
                      id='dadosAssessor'
                      className='form-dialog-aditivo__tipo-contrato-title'
                    >
                      {languageValues.cadastroContratoDadosAditivo}
                    </Typography>
                  </Col>
                </Row> 
                
                <Row>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CustomTextField
                      id={'data-inicio'}
                      label={languageValues.cadastroContratoDataInicioAditivo}
                      name='dataInicio'
                      value={contratosAssessoresReducer.contratoEditing.dataInicio}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value.trim();
                        handleDataChange('dataInicio', value);
                        if (value.length === 10) {
                          onDataInicioChanged(value);
                        } else {
                          setDataInicioError('');
                        }
                      }}
                      InputProps={{
                        inputComponent: DateMaskedInput,
                      }}
                      helperText={
                        dataInicioError ||
                        (!dataInicioValidacaoAditivoResultado.status ? dataInicioValidacaoAditivoResultado.error : '')
                      }
                      error={!dataInicioValidacaoAditivoResultado.status || !!dataInicioError}
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CustomTextField
                      id={'data-fim'}
                      label={languageValues.cadastroContratoDataFimAditivo}
                      name='dataFim'
                      value={contratosAssessoresReducer.contratoEditing.dataFim}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value.trim();
                        handleDataChange('dataFim', value);
                        if (value.length === 10) {
                          onDataFimChanged(value);
                        } else {
                          setDataFimError('');
                        }
                      }}
                      InputProps={{
                        inputComponent: DateMaskedInput,
                      }}
                      helperText={
                        dataFimError ||
                        (!dataFimValidacaoAditivoResultado.status ? dataFimValidacaoAditivoResultado.error : '')
                      }
                      error={!dataFimValidacaoAditivoResultado.status || !!dataFimError}
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                </Row>

                <Row className='form-dialog-aditivo__row-content'>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <div className='form-dialog-aditivo__label-total-meses'>
                      {languageValues.totalMesesContratados}
                    </div>
                    <div className='form-dialog-aditivo__value-total-meses'>
                      {isNaN(
                        contratosAssessoresReducer.contratoEditing.totalMeses
                      )
                        ? 0
                        : Number(
                          contratosAssessoresReducer.contratoEditing
                            .totalMeses
                        ).toString()}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CustomCheckbox
                      id={'analise-juridica'}
                      className={'form-dialog-aditivo__analise-juridica'}
                      label={languageValues.flgAnaliseJuridicaAceite}
                      name='flgAnaliseJuridica'
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        handleDataChange(
                          'flgAnaliseJuridica',
                          event.target.checked
                        );
                      }}
                      checked={
                        contratosAssessoresReducer.contratoEditing
                          .flgAnaliseJuridica
                      }
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                </Row>
              </Box>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions className='form-dialog-aditivo__dialog-actions'>
          <Button
            className='form-dialog-aditivo__btnClose'
            onClick={() => fechaModalEAtualizaContrato()}
          >
            {languageValues.cancelButton}
          </Button>
          <Button
            className='form-dialog-aditivo__btnSave'
            onClick={() => abrirModalCriacaoAditivo()}
            disabled={!validDadosAssessorAditivo()}
          >
            {languageValues.concluir}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(ModalAditivoAssessor)
